import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/axios";
import { useDragReorder } from "../../hooks/useDragReorder";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { motion, AnimatePresence } from "framer-motion";

import {
  FaEdit,
  FaTrash,
  FaGripVertical,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa";
import AdminSearchBar from "../../components/AdminSideComponents/AdminSearchBar";
import { FiInfo } from "react-icons/fi";

const BannerSkeleton = () => (
  <div className="bg-white border rounded-xl shadow-sm p-4 animate-pulse">
    <div className="flex justify-between mb-3">
      <div className="w-10 h-4 bg-gray-200 rounded" />
      <div className="w-14 h-6 bg-gray-200 rounded" />
    </div>
    <div className="w-full aspect-[2160/833] bg-gray-200 rounded-lg" />
    <div className="mt-3 w-24 h-4 bg-gray-200 rounded" />
  </div>
);
const EmptyBannerView = ({ onAdd }) => (
  <div className="p-10 border border-dashed border-gray-300 text-center bg-white rounded-lg">
    <h2 className="text-gray-700 font-medium text-lg mb-2">No Banners Found</h2>
    <p className="text-gray-500 text-sm mb-4">
      You haven't added any hero banners yet. Create the first one now.
    </p>

    <button
      onClick={onAdd}
      className="px-5 py-2 bg-primaryColor text-white rounded-lg hover:opacity-90 transition"
    >
      + Add Banner
    </button>
  </div>
);

const AdminBanners = () => {
  const {
    list: banners,
    original,
    orderChanged,
    initialize,
    onDragStart,
    onDragOver,
    onDrop,
    cancelOrder,
    saveOrder,
    setList: setBanners,
  } = useDragReorder();

  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const resp = await axiosInstance.get("/banner/admin");
      const list = resp?.data?.data ?? [];
      const sorted = list.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
      initialize(sorted);
    } catch (err) {
      toast.error("Failed to load banners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
    // eslint-disable-next-line
  }, []);

  const handleSearch = (q) => {
    if (!q) return setBanners(original);
    const lower = q.toLowerCase();
    setBanners(
      original.filter(
        (x) =>
          (x.title || "").toLowerCase().includes(lower) ||
          (x.sequence + "").includes(lower)
      )
    );
  };

  const handleAddNew = () => {
    navigate("/admin-create-banners");
  };

  const handleEdit = (item) => {
    if (!item?._id) return toast.error("Invalid banner ID");
    navigate(`/admin-edit-banners/${item._id}`);
  };

  const saveOrderToDB = async (updatedList) => {
    await Promise.all(
      updatedList.map((item) =>
        axiosInstance.put(`/banner/${item._id}`, {
          sequence: item.sequence,
        })
      )
    );
    toast.success("Banner order updated");
  };

  const handleSaveOrder = () => saveOrder(saveOrderToDB);

  const handleDelete = (banner) => {
    Swal.fire({
      title: "Delete Banner?",
      text: banner.title,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axiosInstance.delete(`/banner/${banner._id}`);
          setBanners((p) => p.filter((x) => x._id !== banner._id));
          toast.success("Banner deleted");
        } catch (err) {
          toast.error("Delete failed");
        }
      }
    });
  };

  const toggleStatus = async (banner) => {
    try {
      const resp = await axiosInstance.put(`/banner/${banner._id}`, {
        isActive: !banner.isActive,
      });

      const updated = resp?.data?.data ?? resp?.data;
      setBanners((p) => p.map((x) => (x._id === banner._id ? updated : x)));
      toast.success("Status updated");
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSearchBar
        title="Hero Banner Manager"
        onSearchClick={handleSearch}
        onAddNewClick={handleAddNew}
        hideAddButton={false}
      />
      <div className=" flex items-center gap-2 text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded">
        <FiInfo className="text-primaryColor min-w-4 min-h-4" />
        <span>
          Drag cards to adjust their order. Click <strong>Edit</strong> to
          update title and image.
        </span>
      </div>
      {/* Order Changed Notification Bar */}
      {orderChanged && (
        <div className="px-6 mt-2 py-3 bg-yellow-100 border-b border-yellow-300 flex items-center justify-between">
          <span className="text-yellow-800 font-medium">
            Order changed. Save changes?
          </span>
          <div className="flex gap-3">
            <button
              className="px-4 py-1 bg-primaryColor text-white rounded"
              onClick={handleSaveOrder}
            >
              Save Order
            </button>
            <button className="px-4 py-1 border rounded" onClick={cancelOrder}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* CONTENT GRID */}
      <div className="px-6 py-6">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3  gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <BannerSkeleton key={i} />
            ))}
          </div>
        ) : banners.length === 0 ? (
          <EmptyBannerView onAdd={handleAddNew} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3  gap-4">
            <AnimatePresence>
              {banners.map((item, index) => (
                <motion.div
                  key={item._id}
                  layout
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                >
                  <div
                    draggable
                    onDragStart={(e) => onDragStart(e, index)}
                    onDragOver={onDragOver}
                    onDrop={(e) => onDrop(e, index)}
                    className="bg-white border rounded-xl shadow-sm p-4 hover:shadow-lg transition"
                  >
                    {/* TOP BAR */}
                    <div className="flex justify-between mb-3">
                      <div className="flex items-center gap-2 text-gray-500 text-xs">
                        <FaGripVertical className="cursor-move" />
                        <span>Seq {item.sequence}</span>
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-2 rounded hover:bg-gray-100"
                        >
                          <FaEdit className="text-primaryColor" />
                        </button>

                        <button
                          onClick={() => handleDelete(item)}
                          className="p-2 rounded hover:bg-red-50"
                        >
                          <FaTrash className="text-red-600" />
                        </button>
                      </div>
                    </div>

                    {/* IMAGE */}
                    <div className="w-full relative  aspect-[2160/833] overflow-hidden rounded-md bg-gray-200">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        draggable={false}
                      />

                      {/* black gradient overlay left->right */}
                      {item.showGradient !== false && (
                        <div
                          style={{
                            background:
                              "linear-gradient(90deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.35) 30%, rgba(0,0,0,0.0) 100%)",
                          }}
                          className="absolute inset-0 flex items-center"
                        />
                      )}

                      <div className="absolute inset-0 flex items-center pointer-events-none">
                        <div className="px-4 w-full">
                          <div 
                            className={`text-white text-xl font-bold ${!item.showGradient ? 'drop-shadow-lg' : ''}`}
                            title={item.title}
                          >
                            {item.title}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* STATUS */}
                    <div className="mt-3 flex justify-end">
                      <button
                        onClick={() => toggleStatus(item)}
                        className={`flex items-center gap-2 text-xs px-3 py-1 rounded ${
                          item.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-200 text-gray-700"
                        }`}
                      >
                        {item.isActive ? (
                          <FaToggleOn className="text-green-600" />
                        ) : (
                          <FaToggleOff className="text-gray-600" />
                        )}
                        {item.isActive ? "Active" : "Inactive"}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBanners;
